import type { DatabaseMaterial } from '@/hooks/useMaterials';
import { widthsMatch } from '@/lib/materials/taxonomy';

/**
 * Trova i profili "abbinati" a un montante/guida nel catalogo.
 *
 * Questa funzione NON inserisce piu' niente da sola: alimenta il banner
 * "Manca la guida" e propone dei candidati che l'utente sceglie a mano.
 *
 * Perche' e' stata riscritta. La versione precedente ordinava i candidati per
 * somiglianza fra il nome della guida e quello del montante, ma "Guida U 75/40"
 * e "Montante C 75/50" non hanno parole in comune: tutti i candidati facevano
 * punteggio 0, `sort` non riordinava nulla e veniva scelta la PRIMA IN ORDINE
 * ALFABETICO fra quelle di pari larghezza. Da qui i prodotti sbagliati.
 *
 * Ora si ordina per criteri strutturati (fornitore, spessore del lamierino,
 * lunghezza barra, prezzo) e si escludono i profili che non c'entrano nulla
 * con una parete.
 */

/** Profili che stanno nelle categorie struttura ma NON sono guide da parete. */
const NON_WALL_GUIDE_TYPES = new Set([
  'cornice_perimetrale', // perimetrale da controsoffitto
  'angolare_L',          // angolare perimetrale da controsoffitto
  'omega',               // profilo omega per rasature/contropareti incollate
  'guida_esterno',       // sistemi per esterno
]);

/** Profili che stanno in structure_frame ma NON sono montanti da parete. */
const NON_WALL_FRAME_TYPES = new Set([
  'portante_T',        // orditura a T da controsoffitto
  'T24',
  'T15',
  'montante_esterno',  // sistemi per esterno
]);

/**
 * NB: si usa una lista di ESCLUSIONE e non di ammissione perche' il campo
 * `profile_type` e' popolato male: 74 guide su 262 ce l'hanno vuoto e altre 49
 * sono marcate "speciale". Una allowlist scarterebbe la maggioranza del catalogo.
 */
function isPlausibleForWall(m: DatabaseMaterial, targetCategory: string): boolean {
  const t = (m.profile_type ?? '').trim();
  if (!t) return true; // non classificato: non lo si puo' escludere
  return targetCategory === 'structure_guide'
    ? !NON_WALL_GUIDE_TYPES.has(t)
    : !NON_WALL_FRAME_TYPES.has(t);
}

export interface StructureMatchCandidate {
  material: DatabaseMaterial;
  /** Il fornitore e' diverso da quello del profilo gia' scelto: da segnalare in UI. */
  differentSupplier: boolean;
}

export interface FindStructureMatchesOptions {
  /** Materiali che l'org ha nascosto dal catalogo: non vanno proposti. */
  hiddenIds?: Set<string>;
  /** Quanti candidati restituire (default 3). */
  limit?: number;
}

/**
 * Restituisce fino a `limit` candidati, dal piu' plausibile al meno.
 *
 * Si propongono piu' opzioni invece di una sola perche' l'abbinamento resta
 * un'euristica: la larghezza non basta a individuare il prodotto giusto, e
 * far scegliere e' piu' onesto che indovinare.
 */
export function findStructureMatches(
  selectedMaterial: DatabaseMaterial,
  catalog: DatabaseMaterial[],
  targetCategory: 'structure_frame' | 'structure_guide',
  opts: FindStructureMatchesOptions = {},
): StructureMatchCandidate[] {
  const { hiddenIds, limit = 3 } = opts;

  const selWidth = selectedMaterial.width ?? null;
  if (selWidth == null) return [];

  const selSheet = selectedMaterial.sheet_thickness ?? null;
  const selLength = selectedMaterial.length ?? null;
  const selSupplier = selectedMaterial.supplier;

  // Larghezza a TOLLERANZA, non uguaglianza.
  //
  // Le due misure non coincidono quasi mai, perche' la guida deve contenere il
  // montante ed e' 1-2 mm piu' larga: i montanti stanno a 48/49/50, 74/75,
  // 99/100, 124/125, 149/150 mentre le guide solo a 50, 75, 100, 125, 150.
  // Con il confronto esatto, 65 montanti non trovavano NESSUNA guida e il
  // sistema ripiegava su un altro fornitore — il montante RProduzioni da 124
  // proponeva una guida Knauf, con la 125 RProduzioni disponibile.
  const pool = catalog.filter(m =>
    m.category === targetCategory &&
    m.id !== selectedMaterial.id &&
    widthsMatch(m.width, selWidth) &&
    !hiddenIds?.has(m.id) &&
    isPlausibleForWall(m, targetCategory),
  );
  if (pool.length === 0) return [];

  /**
   * La guida deve CONTENERE il montante: a parita' d'altro si preferisce quella
   * di larghezza maggiore o uguale. Cercando il montante per una guida vale
   * l'opposto — il montante deve starci dentro.
   */
  const fitsInside = (m: DatabaseMaterial): number => {
    const w = Number(m.width);
    const sel = Number(selWidth);
    if (!Number.isFinite(w)) return 1;
    const ok = targetCategory === 'structure_guide' ? w >= sel : w <= sel;
    return ok ? 0 : 1;
  };

  // Punteggio: piu' basso = meglio. L'ordine dei criteri e' quello con cui
  // ragiona un posatore — prima la marca, poi lo spessore del lamierino,
  // poi la lunghezza della barra, e a parita' di tutto il piu' economico.
  const rank = (m: DatabaseMaterial): number[] => [
    m.supplier === selSupplier ? 0 : 1,
    fitsInside(m),
    selSheet != null && m.sheet_thickness != null && Number(m.sheet_thickness) === Number(selSheet) ? 0 : 1,
    selLength != null && m.length != null && Number(m.length) === Number(selLength) ? 0 : 1,
    Number(m.unit_price ?? 0),
  ];

  const sorted = [...pool].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    for (let i = 0; i < ra.length; i++) {
      if (ra[i] !== rb[i]) return ra[i] - rb[i];
    }
    return (a.name ?? '').localeCompare(b.name ?? '');
  });

  return sorted.slice(0, limit).map(m => ({
    material: m,
    differentSupplier: m.supplier !== selSupplier,
  }));
}

/**
 * Categoria opposta in coppia montante/guida.
 */
export function oppositeStructureCategory(c: 'structure_frame' | 'structure_guide'): 'structure_frame' | 'structure_guide' {
  return c === 'structure_frame' ? 'structure_guide' : 'structure_frame';
}
